// frontend/src/pages/Dashboard.jsx
// User dashboard with project list, search, filters, seeded badges, and edit/delete actions.

import { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, DarkModeContext } from '../App';
import { getAllProjects, deleteProject, logout as apiLogout } from '../utils/api';

const STATUS_COLORS = {
  CONVERGED: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  CAPPED: 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200',
  NOT_VIABLE: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
  ACTIVE: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
};

const STATUS_OPTIONS = ['ACTIVE', 'CONVERGED', 'CAPPED', 'NOT_VIABLE'];
const MODE_OPTIONS = ['freeform', 'seeded'];

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [modeFilter, setModeFilter] = useState(new Set());

  const { user, logout } = useContext(AuthContext);
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    try {
      const data = await getAllProjects();
      setProjects(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId, projectTitle) => {
    if (!window.confirm(`Delete "${projectTitle}"? This cannot be undone.`)) return;
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  const handleLogout = async () => {
    try { await apiLogout(); } catch (err) { /* ignore */ }
    logout();
    navigate('/');
  };

  const getRecommendationColor = (rec) => {
    if (!rec) return '';
    if (rec === 'NOT VIABLE') return 'text-red-500 dark:text-red-300';
    if (rec.includes('FILE AS-IS')) return 'text-green-600 dark:text-green-400';
    if (rec.includes('FIX MAJOR AND')) return 'text-yellow-600 dark:text-yellow-400';
    if (rec.includes('ADVERSE')) return 'text-orange-600 dark:text-orange-400';
    if (rec.includes('MAJOR REVISION')) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const toggleSet = (set, val) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    return next;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      if (q && !p.inventionTitle.toLowerCase().includes(q)) return false;
      if (statusFilter.size > 0 && !statusFilter.has(p.status)) return false;
      if (modeFilter.size > 0 && !modeFilter.has(p.projectMode || 'freeform')) return false;
      return true;
    });
  }, [projects, search, statusFilter, modeFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-lg text-gray-900 dark:text-gray-100">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <nav className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-gray-900 dark:text-gray-100">Patent Council</Link>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">{user?.displayName}</span>
              <Link to="/settings" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-3 py-2 rounded-md text-sm font-medium">Settings</Link>
              <button onClick={toggleDarkMode} className="p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600" aria-label="Toggle dark mode">
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button onClick={handleLogout} className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-3 py-2 rounded-md text-sm font-medium">Log out</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Your Patent Projects</h1>
          <Link to="/patents/new" className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium">New Patent Project</Link>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 mb-4">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        )}

        {projects.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6 space-y-3">
            <div>
              <label htmlFor="search" className="sr-only">Search projects</label>
              <input
                id="search"
                type="search"
                placeholder="Search by title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <span className="text-gray-500 dark:text-gray-400 mr-1">Status:</span>
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(toggleSet(statusFilter, s))}
                  aria-pressed={statusFilter.has(s)}
                  className={`px-2.5 py-1 rounded-full border ${
                    statusFilter.has(s)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {s}
                </button>
              ))}
              <span className="ml-4 text-gray-500 dark:text-gray-400 mr-1">Mode:</span>
              {MODE_OPTIONS.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModeFilter(toggleSet(modeFilter, m))}
                  aria-pressed={modeFilter.has(m)}
                  className={`px-2.5 py-1 rounded-full border ${
                    modeFilter.has(m)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {m}
                </button>
              ))}
              {(statusFilter.size > 0 || modeFilter.size > 0 || search) && (
                <button
                  type="button"
                  onClick={() => { setStatusFilter(new Set()); setModeFilter(new Set()); setSearch(''); }}
                  className="ml-2 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  clear
                </button>
              )}
              <span className="ml-auto text-gray-500 dark:text-gray-400">
                Showing {filtered.length} of {projects.length}
              </span>
            </div>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No projects yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new patent project.</p>
            <div className="mt-6">
              <Link to="/patents/new" className="inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                New Patent Project
              </Link>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
            No projects match the current filters.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((project) => (
              <div key={project.id} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 flex flex-col">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate flex-1">
                    {project.inventionTitle}
                  </h3>
                  <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[project.status] || STATUS_COLORS.ACTIVE}`}>
                    {project.status}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {project.projectMode === 'seeded' && (
                    <span className="px-2 py-0.5 rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300 text-[10px] uppercase font-semibold">
                      Seeded
                    </span>
                  )}
                  {project.normalizationStatus === 'pending' && project.projectMode === 'seeded' && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] uppercase">
                      Normalization pending
                    </span>
                  )}
                </div>
                <div className="mt-4 space-y-1 text-sm text-gray-500 dark:text-gray-400">
                  <div>Round: {project.currentRound}</div>
                  <div>Score: {project.convergenceScore}</div>
                  {project.filingRecommendation && (
                    <div className={`text-sm font-medium ${getRecommendationColor(project.filingRecommendation)}`}>
                      {project.filingRecommendation}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    Updated {new Date(project.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="mt-6 flex gap-2">
                  {project.status === 'ACTIVE' ? (
                    <Link to={`/sessions/${project.id}`} className="flex-1 text-center bg-blue-600 text-white hover:bg-blue-700 px-3 py-2 rounded text-sm font-medium">
                      Continue
                    </Link>
                  ) : (
                    <Link to={`/results/${project.id}`} className="flex-1 text-center bg-gray-600 text-white hover:bg-gray-700 px-3 py-2 rounded text-sm font-medium">
                      Results
                    </Link>
                  )}
                  <Link
                    to={`/patents/${project.id}/edit`}
                    className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 px-3 py-2 rounded text-sm"
                    title="Edit project"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDeleteProject(project.id, project.inventionTitle)}
                    className="bg-red-600 text-white hover:bg-red-700 px-3 py-2 rounded text-sm font-medium"
                    title="Delete project"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
